import { useMemo } from 'react';
import {
  CandidateRow,
  ElementOriginMapping,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import {
  DiscardTrigger,
  Baseline,
  IElement,
  IProductElement,
  OneOfChildElements,
} from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { selectedProposalIdForVersion } from './import-replace-target.helpers';
import { IProduct } from '@shared/models/product.model';
import { Recipe } from '@shared/models/recipe.model';
import {
  candidateConversion,
  candidateUnitFrom,
  tryMaterializeCandidateRow,
  TryMaterializeCandidateRowContext,
} from '@shared/helpers/materialize-candidate.helpers';
import { isCandidateRowEdited } from '@shared/helpers/candidate-import-fields.helpers';
import type { IfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { MappingsByCode } from './element-imports.store';

/**
 * Derived candidate row status (DEV-3298).
 * - `autoMapped`: materialized by file-drop batch import, no user edits.
 * - `aiMapped`: materialized by applying an AI product suggestion (DEV-3605).
 * - `mapped`: imported and the user touched product / conversion factor / unit
 *   (directly or by editing quantity), or the user drove the import in-row.
 * - `incomplete`: no element yet — whether or not a full mapping exists (a
 *   complete-but-unimported row still surfaces the add affordance).
 * - `discarded`: explicitly discarded by the user (local to this import).
 * - `autoDiscarded`: discarded at import via a saved discard mapping (DEV-3360).
 */
export type CandidateRowStatus =
  | 'autoMapped'
  | 'aiMapped'
  | 'mapped'
  | 'incomplete'
  | 'discarded'
  | 'autoDiscarded';

/** Display order for the status summary counters. */
export const CANDIDATE_STATUS_ORDER: CandidateRowStatus[] = [
  'autoMapped',
  'aiMapped',
  'mapped',
  'incomplete',
  'discarded',
  'autoDiscarded',
];

/** User-facing status label (title-case), also used as a search match target. */
export const CANDIDATE_STATUS_LABEL: Record<CandidateRowStatus, string> = {
  autoMapped: 'Auto-mapped',
  aiMapped: 'Mapped AI-suggested',
  mapped: 'Mapped',
  incomplete: 'Incomplete',
  discarded: 'Skipped',
  autoDiscarded: 'Auto-skipped',
};

/** Collapsed status groups shown in the import-file summary row. */
export type StatusSummaryGroupKey = 'mapped' | 'incomplete' | 'discarded';

export const STATUS_SUMMARY_GROUPS: {
  key: StatusSummaryGroupKey;
  statuses: CandidateRowStatus[];
}[] = [
  { key: 'mapped', statuses: ['autoMapped', 'aiMapped', 'mapped'] },
  { key: 'incomplete', statuses: ['incomplete'] },
  // AUTO-DISCARDED collapses into DISCARDED on the import-file rollup, the same
  // way AUTO-MAPPED collapses into MAPPED above (DEV-3360).
  { key: 'discarded', statuses: ['discarded', 'autoDiscarded'] },
];

/**
 * Hover copy for a file-header status chip: status name plus the click action.
 */
export const statusSummaryFilterTooltip = (
  label: string,
  hidden: boolean,
): string => (hidden ? `${label} - Click to show` : `${label} - Click to hide`);

/** Map a row status to its summary segment key. */
export const getStatusSummaryGroupKey = (
  status: CandidateRowStatus,
): StatusSummaryGroupKey => {
  for (const group of STATUS_SUMMARY_GROUPS) {
    if (group.statuses.includes(status)) return group.key;
  }
  return 'incomplete';
};

/** Whether rows with {@link status} are hidden by the summary filter. */
export const isStatusGroupHidden = (
  status: CandidateRowStatus,
  hiddenGroups: ReadonlySet<StatusSummaryGroupKey>,
): boolean => hiddenGroups.has(getStatusSummaryGroupKey(status));

/**
 * Selected candidate row ids whose status summary group is filtered out —
 * used to drop sticky selection when Skip (or a filter toggle) hides the row.
 */
export const collectStatusFilterHiddenSelectedIds = (
  rowsWithStatus: readonly {
    row: Pick<CandidateRow, 'id'>;
    status: CandidateRowStatus;
  }[],
  selectedIds: ReadonlySet<string>,
  hiddenGroups: ReadonlySet<StatusSummaryGroupKey>,
): string[] => {
  if (hiddenGroups.size === 0 || selectedIds.size === 0) return [];
  return rowsWithStatus
    .filter(
      ({ row, status }) =>
        selectedIds.has(row.id) && isStatusGroupHidden(status, hiddenGroups),
    )
    .map(({ row }) => row.id);
};

/**
 * Selected ids that belong to this file but are not in the currently visible
 * row set (search and/or status filter).
 */
export const collectNonVisibleSelectedIds = (
  selectedIds: ReadonlySet<string>,
  fileRowIds: ReadonlySet<string>,
  visibleRowIds: ReadonlySet<string>,
): string[] => {
  if (selectedIds.size === 0 || fileRowIds.size === 0) return [];
  const hidden: string[] = [];
  for (const id of selectedIds) {
    if (fileRowIds.has(id) && !visibleRowIds.has(id)) hidden.push(id);
  }
  return hidden;
};

/**
 * Toggle a summary segment. Hiding the last visible segment inverts the
 * filter — only that segment is hidden and all other active segments show.
 */
export const toggleStatusSummaryGroupHidden = (
  key: StatusSummaryGroupKey,
  hiddenGroups: ReadonlySet<StatusSummaryGroupKey>,
  activeGroups: ReadonlySet<StatusSummaryGroupKey>,
): Set<StatusSummaryGroupKey> => {
  if (hiddenGroups.has(key)) {
    const next = new Set(hiddenGroups);
    next.delete(key);
    return next;
  }

  const visibleWithRows = STATUS_SUMMARY_GROUPS.filter(
    (group) => activeGroups.has(group.key) && !hiddenGroups.has(group.key),
  );

  const soleVisible = visibleWithRows[0];
  if (visibleWithRows.length === 1 && soleVisible?.key === key) {
    return new Set([key]);
  }

  const next = new Set(hiddenGroups);
  next.add(key);
  return next;
};

/** Products keyed by id, as provided by the product store lookup. */
export type ProductsById = Record<string, IProduct | undefined>;

/** Recipes keyed by id, as provided by the recipe store lookup (DEV-3387). */
export type RecipesById = Record<string, Recipe | undefined>;

export type ResolveCandidateStatusOptions = {
  importsById?: Record<string, ProjectElementImport | undefined>;
  /**
   * Per-row inline mapping overlay from unsaved drafts (localStorage). Drives the
   * status of not-yet-imported rows without writing to the DB mapping table.
   */
  draftMappingsByRowId?: Record<string, ElementOriginMapping | undefined>;
  /** Recipes keyed by id, for re-materializing recipe-mapped rows (DEV-3387). */
  recipesById?: RecipesById;
  ifcMeasureByRowId?: Record<string, IfcMeasure | undefined>;
};

/**
 * The product a candidate row resolves to, preferring a per-row draft mapping
 * over the DB mapping. Returns `undefined` when the row is not (yet) mapped.
 */
/** A draft mapping (if it has a reference) wins over the stored mapping for the row's resource code. */
const resolveDraftOrStoredMapping = (
  draftMapping: ElementOriginMapping | undefined,
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
): ElementOriginMapping | undefined => {
  if (draftMapping?.referenceId) return draftMapping;
  if (!row.resourceCode) return undefined;
  return mappingsByCode[row.resourceCode];
};

export const resolveCandidateProduct = (
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
  productsById: ProductsById,
  draftMapping?: ElementOriginMapping,
): IProduct | undefined => {
  const unitFrom = candidateUnitFrom(row);
  if (!unitFrom) return undefined;

  const mapping = resolveDraftOrStoredMapping(
    draftMapping,
    row,
    mappingsByCode,
  );
  if (mapping?.referenceType !== 'product' || !mapping.referenceId) {
    return undefined;
  }

  const product = productsById[mapping.referenceId];
  if (!product) return undefined;

  const entry = mapping.mappings[unitFrom];
  const manualConversion =
    Boolean(entry?.unitTo) && entry?.quantityFactor !== undefined;
  return manualConversion || candidateConversion(row, product)
    ? product
    : undefined;
};

/**
 * The recipe a candidate row resolves to, preferring a per-row draft mapping
 * over the DB mapping (DEV-3387). Returns `undefined` when the row is not (yet)
 * mapped to a recipe.
 */
export const resolveCandidateRecipe = (
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
  recipesById: RecipesById,
  draftMapping?: ElementOriginMapping,
): Recipe | undefined => {
  if (!candidateUnitFrom(row)) return undefined;

  const mapping = resolveDraftOrStoredMapping(
    draftMapping,
    row,
    mappingsByCode,
  );
  if (mapping?.referenceType !== 'recipe' || !mapping.referenceId) {
    return undefined;
  }
  return recipesById[mapping.referenceId];
};

/** First product child of an element (depth-first), if any. */
const findFirstProductElement = (
  element: IElement,
): IProductElement | undefined => {
  for (const child of element.elements) {
    if (projectGuards.is.productElement(child)) return child;
    if (projectGuards.is.element(child)) {
      const nested = findFirstProductElement(child);
      if (nested) return nested;
    }
  }
  return undefined;
};

/**
 * The generic product id a materialized import row is mapped to (DEV-3369), so
 * the Imports-tab product picker reflects the mapping used at creation. Reads
 * the element's {@link ElementOrigin} mapping (inline snapshot, or the DB
 * mapping for `stored`-source origins), falling back to the element's first
 * product child for product-category elements.
 */
export const resolveMappedRowProductId = (
  element: IElement,
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
): string | undefined => {
  const mapping = resolveOriginMapping(element.origin, row, mappingsByCode);
  if (mapping?.referenceType === 'product' && mapping.referenceId) {
    return mapping.referenceId;
  }
  const product = findFirstProductElement(element);
  return product?.generic_product_id ?? product?.product_id;
};

/**
 * The recipe id a materialized import row is mapped to (DEV-3387), so the
 * Imports-tab picker reflects the recipe used at creation. Reads the element's
 * {@link ElementOrigin} mapping, falling back to the element's own `recipe_id`.
 */
export const resolveMappedRowRecipeId = (
  element: IElement,
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
): string | undefined => {
  const mapping = resolveOriginMapping(element.origin, row, mappingsByCode);
  if (mapping?.referenceType === 'recipe' && mapping.referenceId) {
    return mapping.referenceId;
  }
  return element.recipe_id ?? undefined;
};

/** The mapping backing an imported element: inline snapshot, or the DB mapping for `stored`-source origins. */
const resolveOriginMapping = (
  origin: IElement['origin'],
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
): ElementOriginMapping | MappingsByCode[string] | undefined => {
  if (origin?.kind !== 'csvImport') return undefined;
  if (origin.source === 'inline') return origin.mapping;
  if (!row.resourceCode) return undefined;
  return mappingsByCode[row.resourceCode];
};

/** Imported-row lookup: candidate-row id → the element materialized from it. */
export type ImportedElementsByRowId = ReadonlyMap<string, IElement>;

const EMPTY_IMPORTED: ImportedElementsByRowId = new Map<string, IElement>();

const importedElementsCache = new WeakMap<
  Baseline,
  Map<string, ImportedElementsByRowId>
>();

/**
 * Whether an imported element belongs in the view for {@link proposalId}.
 * `undefined` is explicit baseline: only elements that no proposal owns.
 * A selected proposal also sees baseline-owned imports, which live in the shared tree.
 */
const importVisibleInProposal = (
  owner: ProposalId | undefined,
  proposalId: ProposalId | undefined,
): boolean => {
  if (!proposalId) return owner === undefined;
  return owner === undefined || owner === proposalId;
};

/**
 * Lookup of candidate-row id → materialized project element visible in
 * {@link proposalId}. An element imported into another proposal is omitted, so
 * the row can be imported there without removing the other proposal's element.
 * Pass `undefined` for explicit baseline.
 */
export const collectImportedElements = (
  version: Baseline | undefined,
  proposalId: ProposalId | undefined,
): ImportedElementsByRowId => {
  if (!version) return EMPTY_IMPORTED;

  const cacheKey = proposalId ?? 'baseline';
  const byProposal =
    importedElementsCache.get(version) ??
    new Map<string, ImportedElementsByRowId>();
  const cached = byProposal.get(cacheKey);
  if (cached) return cached;

  const byRowId = new Map<string, IElement>();
  const walk = (
    elements: OneOfChildElements[],
    ancestorOwner: ProposalId | undefined,
  ): void => {
    for (const el of elements) {
      if (!projectGuards.is.element(el)) continue;
      const owner = el.createdInProposalId ?? ancestorOwner;
      if (
        el.origin?.candidateRowId &&
        importVisibleInProposal(owner, proposalId)
      ) {
        byRowId.set(el.origin.candidateRowId, el);
      }
      walk(el.elements, owner);
    }
  };
  walk(version.elements, undefined);

  byProposal.set(cacheKey, byRowId);
  importedElementsCache.set(version, byProposal);
  return byRowId;
};

/** {@link collectImportedElements} for the proposal the open version is showing. */
export const collectImportedElementsInView = (
  version: Baseline | undefined,
): ImportedElementsByRowId =>
  collectImportedElements(
    version,
    version ? selectedProposalIdForVersion(version) : undefined,
  );

/** Subscribes to proposal selection so a memo recomputes when the proposal changes. */
export const useImportedElements = (
  version: Baseline | undefined,
): ImportedElementsByRowId => {
  const proposalId = useSelectedProposalId();
  return useMemo(
    () => collectImportedElements(version, proposalId),
    [version, proposalId],
  );
};

const buildMaterializeContext = (
  mappingsByCode: MappingsByCode,
  productsById: ProductsById,
  importsById: Record<string, ProjectElementImport | undefined>,
  draftMappingsByRowId?: Record<string, ElementOriginMapping | undefined>,
  recipesById?: RecipesById,
  ifcMeasureByRowId?: Record<string, IfcMeasure | undefined>,
): TryMaterializeCandidateRowContext => ({
  mappingsByCode,
  productsById,
  recipesById,
  importsById,
  draftMappingsByRowId,
  ifcMeasureByRowId,
});

/**
 * Re-materialize the import baseline for an already-imported row from the
 * mapping recorded on the element's {@link IElement.origin}: the inline snapshot
 * when unsaved, or the referenced DB mapping when promoted. Self-contained, so
 * an imported row keeps its matched/edited status without re-deriving from a
 * (possibly cleared) draft. Returns the mapping's `referenceId` for the
 * edited-field comparison and the baseline element, or `undefined` when the
 * mapping can no longer be resolved.
 */
type BaselineResult = { element: IElement; referenceId: string | undefined };

/**
 * Per-live-element baseline cache. Materializing the baseline is a full element
 * build (UUIDs, expressions, category application); without this it re-runs on
 * every row mount — and rows mount/unmount on scroll. Keyed on the live element
 * (a fresh object on every immutable project edit, so the entry self-invalidates)
 * plus the mapping source object identity (`origin.mapping` for inline, the DB
 * mapping row for `stored`), so a remapped row recomputes.
 */
const baselineCache = new WeakMap<
  IElement,
  { key: object; result: BaselineResult | undefined }
>();

const materializeBaselineFromOrigin = (
  row: CandidateRow,
  liveElement: IElement,
  ctx: TryMaterializeCandidateRowContext,
): BaselineResult | undefined => {
  const origin = liveElement.origin;
  if (origin?.kind !== 'csvImport') return undefined;

  // Stable identity for the mapping behind this baseline — the inline snapshot
  // carried on the element, or the DB mapping row (both reused across renders).
  const sourceMapping: object | undefined =
    origin.source === 'inline'
      ? origin.mapping
      : row.resourceCode
        ? ctx.mappingsByCode[row.resourceCode]
        : undefined;

  const mapping: ElementOriginMapping | undefined =
    origin.source === 'inline'
      ? origin.mapping
      : row.resourceCode
        ? mappingsToOriginMapping(ctx.mappingsByCode[row.resourceCode])
        : undefined;
  if (!mapping?.referenceId || !sourceMapping) return undefined;

  const cached = baselineCache.get(liveElement);
  if (cached && cached.key === sourceMapping) return cached.result;

  // Feed the recorded mapping in as the row's draft overlay so the same
  // materialization path produces the baseline element.
  const result = tryMaterializeCandidateRow(row, {
    ...ctx,
    draftMappingsByRowId: { [row.id]: mapping },
  });
  const baseline = result.ok
    ? { element: result.element, referenceId: mapping.referenceId }
    : undefined;
  baselineCache.set(liveElement, { key: sourceMapping, result: baseline });
  return baseline;
};

/** Narrow a full DB mapping to the {@link ElementOriginMapping} subset. */
const mappingsToOriginMapping = (
  mapping: MappingsByCode[string] | undefined,
): ElementOriginMapping | undefined =>
  mapping
    ? {
        referenceId: mapping.referenceId,
        referenceType: mapping.referenceType,
        mappings: mapping.mappings,
      }
    : undefined;

/**
 * Resolve a candidate row's derived status.
 *
 * Priority: discarded/autoDiscarded → (element: edited / user-driven → mapped,
 * AI-applied unedited → aiMapped, else auto-import → autoMapped) → incomplete.
 * A row with no element is always `incomplete`, even when a full mapping
 * exists (the add affordance is gated separately on
 * `isCandidateRowMaterializable`).
 */
export const resolveCandidateStatus = (
  row: CandidateRow,
  mappingsByCode: MappingsByCode,
  discardTriggerByRowId: Record<string, DiscardTrigger>,
  productsById: ProductsById,
  importedElements: ImportedElementsByRowId,
  options: ResolveCandidateStatusOptions = {},
): CandidateRowStatus => {
  const discardTrigger = discardTriggerByRowId[row.id];
  if (discardTrigger) {
    return discardTrigger === 'auto' ? 'autoDiscarded' : 'discarded';
  }

  const ctx = buildMaterializeContext(
    mappingsByCode,
    productsById,
    options.importsById ?? {},
    options.draftMappingsByRowId,
    options.recipesById,
    options.ifcMeasureByRowId,
  );
  const liveElement = importedElements.get(row.id);

  if (liveElement) {
    // A user-driven import (in-row mapping / side-panel save) is MAPPED even
    // before any further edit; only file-drop batch import (`trigger: 'auto'`)
    // is AUTO-MAPPED and AI apply (`trigger: 'ai'`) is MAPPED AI-SUGGESTED.
    // Legacy elements lack the flag → treat as auto.
    const trigger = liveElement.origin?.trigger;
    if (trigger === 'user') return 'mapped';

    const uneditedStatus: CandidateRowStatus =
      trigger === 'ai' ? 'aiMapped' : 'autoMapped';

    // Baseline comes from the element's own origin mapping, not the current DB
    // mapping — so an imported row keeps its status even when its mapping lives
    // only inline. When the mapping can't be resolved, treat as the unedited
    // status for this trigger.
    const baseline = materializeBaselineFromOrigin(row, liveElement, ctx);
    if (!baseline) return uneditedStatus;

    return isCandidateRowEdited(
      liveElement,
      baseline.element,
      baseline.referenceId,
    )
      ? 'mapped'
      : uneditedStatus;
  }

  return 'incomplete';
};

/** Rows materialized or discarded vs total candidate rows for one import. */
export type ImportProgress = {
  materialized: number;
  discarded: number;
  resolved: number;
  total: number;
  /** Whole percent, 0–100. */
  percent: number;
};

/**
 * Import completion from status counts: autoMapped + aiMapped + mapped +
 * discarded + autoDiscarded vs total. Rows that are imported but still
 * {@link CandidateRowStatus.incomplete} do not count as completed, so progress
 * stays consistent with the status icons.
 */
export const computeImportProgressFromStatusCounts = (
  counts: Partial<Record<CandidateRowStatus, number>>,
  total: number,
): ImportProgress => {
  const materialized =
    (counts.autoMapped ?? 0) + (counts.aiMapped ?? 0) + (counts.mapped ?? 0);
  const discarded = (counts.discarded ?? 0) + (counts.autoDiscarded ?? 0);
  const resolved = materialized + discarded;
  const percent = total === 0 ? 0 : Math.round((resolved / total) * 100);
  return { materialized, discarded, resolved, total, percent };
};
